import { type SerializedCompStructure } from '@wix/document-services-types'
import { keys } from '@wix/wix-data-client-common-standalone'

import {
  type DataBindingConnectionConfigByRole,
  type DataBindingRole,
} from '../types'
import { assertConnectionList } from './assertConnectionList'
import { assertNonNullable } from './assertNonNullable'

const setComponentConnectionConfig = <Role extends DataBindingRole>({
  component,
  role,
  config,
}: {
  component: SerializedCompStructure
  role: Role
  config: DataBindingConnectionConfigByRole[Role]
}): SerializedCompStructure => {
  const connectionList = component.connections
  assertNonNullable(connectionList)
  assertConnectionList(connectionList)
  const { items } = connectionList
  for (let index = 0; index < items.length; index += 1) {
    const connection = items[index]
    if (connection.type === 'ConnectionItem' && connection.role === role) {
      return {
        ...component,
        connections: {
          ...connectionList,
          items: [
            ...items.slice(0, index),
            { ...connection, config: JSON.stringify(config) },
            ...items.slice(index + 1),
          ],
        },
      }
    }
  }
  throw new Error(`No connection with role "${role}" found.`)
}

export const setComponentConnectionConfigs = ({
  component,
  configByRole,
}: {
  component: SerializedCompStructure
  configByRole: {
    [Role in DataBindingRole]?: DataBindingConnectionConfigByRole[Role]
  }
}): SerializedCompStructure =>
  keys(configByRole).reduce(
    (component, role) =>
      (<Role extends DataBindingRole>(role: Role) => {
        const config = configByRole[role]
        return config !== undefined
          ? setComponentConnectionConfig({ component, role, config })
          : component
      })(role),
    component,
  )
